import type { ApiError, Me } from "../shared/types";

/** Calls the Kontour API. Redirects to sign-in on 401 and throws the server's message on other errors. */
export async function api<T>(path: string, options: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(path, {
    method: options.method ?? "GET",
    headers: options.method && options.method !== "GET" ? { "Content-Type": "application/json" } : {},
    body: options.method && options.method !== "GET" ? JSON.stringify(options.body ?? {}) : undefined,
  });
  if (res.status === 401 && !/^\/api\/(staff\/)?login/.test(path)) {
    location.href = path.startsWith("/api/staff/") ? "/console/login" : "/login";
    throw new Error("Not signed in");
  }
  const body = (await res.json().catch(() => ({}))) as T | Partial<ApiError>;
  if (!res.ok) {
    const { error, code } = body as Partial<ApiError>;
    throw Object.assign(new Error(error ?? "Something went wrong. Please try again."), { code });
  }
  return body as T;
}

export const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

export function $<T extends HTMLElement = HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing #${id}`);
  return node as T;
}

/**
 * Your account details. Signed-in pages arrive with them (the Worker adds #boot-me), which saves a round
 * trip; later calls (after a change) ask the server again.
 */
export async function loadMe(): Promise<Me> {
  const boot = document.getElementById("boot-me");
  if (boot) {
    boot.remove();
    try {
      return JSON.parse(boot.textContent ?? "") as Me;
    } catch {
      /* fall through to the API */
    }
  }
  return api<Me>("/api/me");
}
